import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Globe, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { getSessionId } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_LOCATIONS = [
  { name: "Ca redémarre", address: "99 rue de Charonne", city: "Paris" },
  { name: "Pignon sur rue", address: "Boulevard Richard Lenoir", city: "Paris" },
  { name: "Roue libre électrique", address: "", city: "Rennes" }
];

export default function ContactPage() {
  const { language } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [partnerStores, setPartnerStores] = useState([]);

  useEffect(() => {
    // Load saved customer data from localStorage for name and email
    const savedCustomer = localStorage.getItem('belleville_customer');
    if (savedCustomer) {
      try {
        const parsed = JSON.parse(savedCustomer);
        setForm(prev => ({
          ...prev,
          name: parsed.prenom && parsed.nom ? `${parsed.prenom} ${parsed.nom}` : prev.name,
          email: parsed.email || prev.email
        }));
      } catch (e) {
        // Error handled silently
      }
    }
    
    axios.post(`${API}/visitor/log`, {
      page: 'contact',
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer
    }).catch(() => {});

    // Fetch partner stores
    axios.get(`${API}/partner-stores`)
      .then(res => {
        if (res.data && res.data.length > 0) {
          setPartnerStores(res.data);
        } else {
          setPartnerStores(DEFAULT_LOCATIONS);
        }
      })
      .catch(() => {
        setPartnerStores(DEFAULT_LOCATIONS);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message
      });
      
      if (response.data.status === 'success') {
        toast.success(language === 'fr' 
          ? 'Message envoyé ! Nous vous répondrons rapidement.'
          : 'Message sent! We will respond shortly.');
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.success(language === 'fr' 
          ? 'Message enregistré. Nous vous contacterons bientôt.'
          : 'Message saved. We will contact you soon.');
        setForm({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      // Error handled silently
      toast.error(language === 'fr' 
        ? 'Erreur lors de l\'envoi. Veuillez réessayer.'
        : 'Error sending message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            {language === 'fr' ? 'Contactez-nous' : 'Contact us'}
          </span>
          <h1 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight mt-2">
            Contact
          </h1>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-8"
          >
            {/* Workshop Info */}
            <div className="p-6 bg-secondary">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">
                {language === 'fr' ? 'Notre atelier' : 'Our workshop'}
              </h2>
              <p className="font-body text-muted-foreground leading-relaxed">
                {language === 'fr' 
                  ? "Atelier de fabrication basé près de Milly-la-Forêt. L'atelier n'est pas ouvert au public."
                  : "Manufacturing workshop based near Milly-la-Forêt. The workshop is not open to the public."}
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-heading font-bold uppercase">Email</h3>
                  <a 
                    href="mailto:bellevillemachine@gmail.com" 
                    className="font-mono text-sm text-[#0047AB] hover:underline mt-1 block"
                  >
                    bellevillemachine@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-heading font-bold uppercase">
                    {language === 'fr' ? 'Téléphone' : 'Phone'}
                  </h3>
                  <a 
                    href="tel:+33766702077" 
                    className="font-mono text-sm mt-1 block hover:text-[#0047AB]"
                  >
                    07 66 70 20 77
                  </a>
                  <p className="font-mono text-xs text-muted-foreground mt-2">
                    {language === 'fr' 
                      ? 'Du lundi au vendredi de 9h à 17h'
                      : 'Monday to Friday, 9am to 5pm'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-heading font-bold uppercase">Site web</h3>
                  <a 
                    href="https://www.bellevillemachine.com" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-sm text-[#0047AB] hover:underline mt-1 block"
                  >
                    www.bellevillemachine.com
                  </a>
                </div>
              </div>
            </div>

            {/* Test Ride Locations */}
            <div className="mt-8">
              <div className="flex items-center gap-3 mb-4">
                <Store className="w-5 h-5" />
                <h2 className="font-heading font-bold text-xl uppercase">
                  {language === 'fr' ? 'Points d\'essai' : 'Test ride locations'}
                </h2>
              </div>
              <p className="font-body text-sm text-muted-foreground mb-4">
                {language === 'fr' 
                  ? 'Retrouvez nos vélos dans ces boutiques partenaires :'
                  : 'Find our bikes at these partner shops:'}
              </p>
              
              <div className="space-y-3">
                {partnerStores.map((location, index) => (
                  <div 
                    key={location.id || index}
                    className="p-4 border border-border hover:border-black transition-colors"
                  >
                    <p className="font-heading font-bold uppercase">{location.name}</p>
                    <p className="font-mono text-xs text-muted-foreground mt-1">
                      {location.address && `${location.address}, `}{location.city}
                    </p>
                    {location.phone && (
                      <p className="font-mono text-xs mt-1">
                        <a href={`tel:${location.phone}`} className="hover:text-[#0047AB]">{location.phone}</a>
                      </p>
                    )}
                    {location.website && (
                      <p className="font-mono text-xs mt-1">
                        <a href={location.website} target="_blank" rel="noopener noreferrer" className="hover:text-[#0047AB]">{location.website}</a>
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="font-mono text-xs uppercase tracking-widest">
                    {language === 'fr' ? 'Nom' : 'Name'}
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 rounded-none"
                    required
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 rounded-none"
                    required
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="subject" className="font-mono text-xs uppercase tracking-widest">
                  {language === 'fr' ? 'Sujet' : 'Subject'}
                </Label>
                <Input
                  id="subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="mt-2 rounded-none"
                  required
                  data-testid="contact-subject"
                />
              </div>

              <div>
                <Label htmlFor="message" className="font-mono text-xs uppercase tracking-widest">
                  Message
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 rounded-none min-h-[150px]"
                  required
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full h-12 rounded-none font-mono uppercase tracking-widest"
                data-testid="contact-submit"
              >
                {sending 
                  ? (language === 'fr' ? 'Envoi en cours...' : 'Sending...') 
                  : (
                    <>
                      {language === 'fr' ? 'Envoyer' : 'Send'}
                      <Send className="ml-2 w-4 h-4" />
                    </>
                  )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
